import { type FC, Fragment, useMemo, useCallback } from 'react';

import { StructuredData } from '@vakantiesnl/components/src/__LEGACY__/atoms/StructuredData';
import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { Link } from '@vakantiesnl/components/src/atoms/Link';
import { useApplicationStore } from '@vakantiesnl/services/src/stores/applicationStore';
import { type BreadCrumbItem } from '@vakantiesnl/types/src/BreadCrumbs';
import { isUndefined, pickBy } from 'lodash';
import { type BreadcrumbList, type ListItem } from 'schema-dts';

import { useCustomStyles } from './BreadCrumbs.style';

export interface BreadCrumbsProps {
	homeUrl?: string;
	breadcrumbs?: BreadCrumbItem[];
	className?: string;
	/** To track the breadcrumb clicks */
	isTracked?: boolean;
	onBreadCrumbClick?: (destinationUrl: string, destinationType?: BreadCrumbItem['destinationType']) => void;
}

const getBreadcrumbsSchemaItem = (breadcrumbs: BreadCrumbItem[], origin: string): BreadcrumbList => {
	return {
		'@type': 'BreadcrumbList',
		itemListElement: breadcrumbs.map((breadcrumb, position: number) => {
			return pickBy(
				{
					'@type': 'ListItem',
					position: position + 1,
					name: breadcrumb.text,
					item: breadcrumb.to && origin && `${origin}${breadcrumb.to}`,
				},
				(val) => !isUndefined(val),
			) as unknown as ListItem;
		}),
	} as unknown as BreadcrumbList;
};

export const BreadCrumbs: FC<BreadCrumbsProps> = ({
	breadcrumbs = [],
	className,
	isTracked,
	onBreadCrumbClick,
	homeUrl = '/',
}) => {
	const { classes: styles } = useCustomStyles();

	const { dynamicOrigin: origin } = useApplicationStore((s) => s);

	const breadCrumbsSchemaItem = useMemo(() => getBreadcrumbsSchemaItem(breadcrumbs, origin), [breadcrumbs, origin]);

	const onClickTracker = useCallback(
		(destinationUrl: string, destinationType?: BreadCrumbItem['destinationType']) => (): void => {
			if (isTracked && !isUndefined(onBreadCrumbClick)) {
				onBreadCrumbClick(destinationUrl, destinationType);
			}
		},
		[isTracked, onBreadCrumbClick],
	);

	return (
		<div className={className} data-cy="breadcrumbs">
			<div className={styles.breadcrumbWrapper}>
				<Link href={homeUrl} passHref>
					<span className={styles.homeBreadcrumb}>
						<Icon type="home" className={styles.icon} />
					</span>
				</Link>
				<span className={styles.divider}>/</span>
				<StructuredData schemaItem={breadCrumbsSchemaItem} id="breadcrumbItem" />
				{breadcrumbs.map((breadcrumb, index) => (
					<Fragment key={index}>
						{breadcrumb.to && index !== breadcrumbs.length - 1 ? (
							<Link
								href={breadcrumb.to}
								passHref
								onClick={onClickTracker(breadcrumb.to, breadcrumb.destinationType)}
								className={styles.breadcrumb}
							>
								{breadcrumb.text}
							</Link>
						) : (
							<span className={styles.nonLinkedBreadcrumb}>{breadcrumb.text}</span>
						)}
						{index !== breadcrumbs.length - 1 && <span className={styles.divider}>/</span>}
					</Fragment>
				))}
			</div>
		</div>
	);
};
